import {
    DEFAULT_DESIGN_SETTINGS,
    DEFAULT_LAYOUT_MAPPING,
    DESIGN_MODULE_CATALOG,
    FLOW_BLOCK_IDS,
    FLOW_MODULE_IDS,
    PNC_CATALOG_MODULE_IDS
} from './constants';

const clone = (value) => JSON.parse(JSON.stringify(value));

const findModule = (moduleId) => DESIGN_MODULE_CATALOG.find(module => module.id === moduleId);

export const normalizeDesignSettings = (settings = {}) => {
    const normalized = clone(DEFAULT_DESIGN_SETTINGS);

    normalized.version = settings.version || normalized.version;
    normalized.configured = Boolean(settings.configured);
    normalized.styling = {
        ...normalized.styling,
        ...(settings.styling || {}),
        hexPrimary: settings.styling?.hexPrimary || settings.styling?.hex || normalized.styling.hexPrimary,
        hexSecondary: settings.styling?.hexSecondary || normalized.styling.hexSecondary,
        cmykPrimary: {
            ...normalized.styling.cmykPrimary,
            ...(settings.styling?.cmykPrimary || settings.styling?.cmyk || {})
        },
        cmykSecondary: {
            ...normalized.styling.cmykSecondary,
            ...(settings.styling?.cmykSecondary || {})
        }
    };
    if (normalized.styling.source === 'manual') {
        normalized.styling.source = 'hex';
    }

    normalized.modules = DESIGN_MODULE_CATALOG.reduce((acc, module) => {
        acc[module.id] = {
            ...clone(module.defaultConfig),
            ...(settings.modules?.[module.id] || {})
        };
        return acc;
    }, {});

    if (!normalized.modules.pncCatalog?.enabled) {
        PNC_CATALOG_MODULE_IDS.forEach(moduleId => {
            normalized.modules[moduleId] = {
                ...normalized.modules[moduleId],
                enabled: false
            };
        });
    }

    const enabledFlowIds = FLOW_MODULE_IDS.filter(moduleId => normalized.modules[moduleId]?.enabled);
    if (enabledFlowIds.length === 0) {
        normalized.modules.processLine = {
            ...normalized.modules.processLine,
            enabled: true
        };
    } else if (enabledFlowIds.length > 1) {
        const keep = enabledFlowIds[0];
        FLOW_MODULE_IDS.forEach(moduleId => {
            if (moduleId !== keep) {
                normalized.modules[moduleId] = {
                    ...normalized.modules[moduleId],
                    enabled: false
                };
            }
        });
    }

    return normalized;
};

export const getEnabledDesignModules = (settings = {}) => {
    const normalized = normalizeDesignSettings(settings);
    return DESIGN_MODULE_CATALOG.filter(module => normalized.modules[module.id]?.enabled);
};

export const buildLayoutFromDesignSettings = (settings = {}) => {
    const normalized = normalizeDesignSettings(settings);
    const column1 = [];
    const column2 = [];
    const pushEnabled = (moduleId, target = 'column1') => {
        const module = findModule(moduleId);
        if (module && normalized.modules[moduleId]?.enabled) {
            (target === 'column2' ? column2 : column1).push(module.blockId);
        }
    };

    pushEnabled('logo', ['top-right', 'bottom'].includes(normalized.modules.logo?.placement) ? 'column2' : 'column1');
    pushEnabled('pncCatalog');
    if (normalized.modules.pncCatalog?.enabled) {
        pushEnabled('pncSearch');
        pushEnabled('productTypeCards');
    }
    pushEnabled('needsConfigurator');
    pushEnabled('imageCarousel');
    if (normalized.modules.pncCatalog?.enabled) {
        pushEnabled('filter');
    }

    column1.push('features-view', 'wizard-navigation');

    column2.push('selections-overview');
    if (normalized.modules.pncCatalog?.enabled) {
        pushEnabled('compare', 'column2');
    }
    pushEnabled('showPnc', 'column2');
    pushEnabled('sendEmail', 'column2');

    return {
        column1: column1.length ? column1 : DEFAULT_LAYOUT_MAPPING.column1,
        column2: column2.length ? column2 : DEFAULT_LAYOUT_MAPPING.column2
    };
};

export const INTRO_BLOCK_ID = 'intro-panel';

export const isIntroFrontPageEnabled = (settings = {}) => (
    Boolean(normalizeDesignSettings(settings).modules.intro?.enabled)
);

export const getFlowMode = (settings = {}) => {
    const normalized = normalizeDesignSettings(settings);
    return FLOW_MODULE_IDS.find(moduleId => normalized.modules[moduleId]?.enabled) || 'processLine';
};

export const sanitizeLayoutMapping = (mapping = {}) => {
    const source = mapping || {};
    const hidden = new Set([INTRO_BLOCK_ID, ...FLOW_BLOCK_IDS]);
    return {
        column1: (source.column1 || []).filter(block => !hidden.has(block)),
        column2: (source.column2 || []).filter(block => !hidden.has(block))
    };
};

export const withoutIntroPanel = (mapping = {}) => sanitizeLayoutMapping(mapping);

export const updateDesignModule = (settings, moduleId, patch) => {
    const normalized = normalizeDesignSettings(settings);
    normalized.modules[moduleId] = {
        ...normalized.modules[moduleId],
        ...patch
    };
    if (FLOW_MODULE_IDS.includes(moduleId) && patch.enabled) {
        FLOW_MODULE_IDS.forEach(flowModuleId => {
            if (flowModuleId !== moduleId) {
                normalized.modules[flowModuleId] = {
                    ...normalized.modules[flowModuleId],
                    enabled: false
                };
            }
        });
    }
    if (moduleId === 'pncCatalog' && patch.enabled === false) {
        PNC_CATALOG_MODULE_IDS.forEach(childModuleId => {
            normalized.modules[childModuleId] = {
                ...normalized.modules[childModuleId],
                enabled: false
            };
        });
    }
    if (PNC_CATALOG_MODULE_IDS.includes(moduleId) && patch.enabled) {
        normalized.modules.pncCatalog = {
            ...normalized.modules.pncCatalog,
            enabled: true
        };
    }
    return normalized;
};

export const updateDesignStyling = (settings, patch) => {
    const normalized = normalizeDesignSettings(settings);
    normalized.styling = {
        ...normalized.styling,
        ...patch,
        cmykPrimary: patch.cmykPrimary
            ? { ...normalized.styling.cmykPrimary, ...patch.cmykPrimary }
            : normalized.styling.cmykPrimary,
        cmykSecondary: patch.cmykSecondary
            ? { ...normalized.styling.cmykSecondary, ...patch.cmykSecondary }
            : normalized.styling.cmykSecondary
    };
    return normalized;
};

export const cmykToHex = ({ c = 0, m = 0, y = 0, k = 0 } = {}) => {
    const clamp = (value) => Math.max(0, Math.min(100, Number(value) || 0)) / 100;
    const cyan = clamp(c);
    const magenta = clamp(m);
    const yellow = clamp(y);
    const black = clamp(k);
    const toHex = (value) => Math.round(255 * (1 - value) * (1 - black)).toString(16).padStart(2, '0');
    return `#${toHex(cyan)}${toHex(magenta)}${toHex(yellow)}`;
};
